import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import React, { useCallback, useEffect, useState } from 'react'

import {
  useCreateBillingNoteMutation,
  useLazyGetAllBillingNotesQuery,
  usePatchBillingNoteMutation,
  useUpdateBillingNoteMutation,
} from 'api/notes/billingNotesCrud'
import HHAccordion from 'components/common/HHAccordion'
import { getArchivedAndUnarchivedNotes, getSortedNotes } from 'data/notes/notesSelectors'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

import ViewArchivedNotesAction from '../common/actions/ViewArchivedNotesAction'
import AddUpdateNoteDialog from '../common/AddUpdateNoteDialog'
import NoteSkeleton from '../common/NoteSkeleton'
import ViewArchiveNotesDialog from '../common/ViewArchiveNotesDialog'

import BillingNotes from './BillingNotes'

type BillingNotesAccordionVariantProps = {
  accountId?: string
}

const BillingNotesAccordionVariant = ({ accountId = '' }: BillingNotesAccordionVariantProps) => {
  const [getAllBillingNotes, { isFetching: isGetAllNotesLoading, data: notesData }] = useLazyGetAllBillingNotesQuery()
  const [isOpenAddUpdateNoteDialog, setIsOpenAddUpdateNoteDialog] = useState(false)
  const [isOpenViewArchiveNoteDialog, setIsOpenViewArchiveNoteDialog] = useState(false)

  const sortedNotes = getSortedNotes({ notes: get(notesData, 'notes', []) })
  const { unarchivedNotes: notes, archivedNotes } = getArchivedAndUnarchivedNotes({ notes: sortedNotes })

  const handleOpenAddBillingNote = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    setIsOpenAddUpdateNoteDialog(true)
  }, [])

  const handleViewArchiveClick = useCallback(() => setIsOpenViewArchiveNoteDialog(true), [])

  const handleCloseViewArchiveDialog = useCallback(() => setIsOpenViewArchiveNoteDialog(false), [])

  const handleCloseAddBillingNote = useCallback(() => {
    setIsOpenAddUpdateNoteDialog(false)
  }, [])

  useEffect(() => {
    if (accountId) {
      getAllBillingNotes({ id: accountId }).unwrap().catch(handleError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId])

  return (
    <>
      <HHAccordion
        isExpandCollapseHeightSame
        isExpanded
        showCustomActions
        withNewColors
        customActionContent={
          <Box alignItems="center" display="flex">
            <IconButton onClick={handleOpenAddBillingNote}>
              <AddIcon />
            </IconButton>
            <ViewArchivedNotesAction archivedNotes={archivedNotes} onViewArchiveClick={handleViewArchiveClick} />
          </Box>
        }
        showActionIcon={false}
        summary={`${T.BILLING_NOTE}s`}
      >
        {isGetAllNotesLoading && <NoteSkeleton rows={3} />}
        {!isGetAllNotesLoading && (
          // @ts-expect-error BillingNotes is an untyped JS component
          <BillingNotes accountId={accountId} notes={notes} />
        )}
      </HHAccordion>
      <AddUpdateNoteDialog
        showPciWarning
        id={accountId}
        isOpen={isOpenAddUpdateNoteDialog}
        title={T.NEW_BILLING_NOTE}
        useCreateNoteMutation={useCreateBillingNoteMutation}
        useUpdateNoteMutation={useUpdateBillingNoteMutation}
        onClose={handleCloseAddBillingNote}
      />
      <ViewArchiveNotesDialog
        id={accountId}
        isOpen={isOpenViewArchiveNoteDialog}
        notes={archivedNotes}
        title={T.ARCHIVED_BILLING_NOTES}
        useUnarchiveNoteMutation={usePatchBillingNoteMutation}
        onClose={handleCloseViewArchiveDialog}
      />
    </>
  )
}

export default BillingNotesAccordionVariant
